import React, { useRef, useMemo, useState, useEffect } from 'react';
import Select from 'react-select';
import ReactToPrint from 'react-to-print';
import { ImFilePdf } from 'react-icons/im';
import { useNavigationContext } from '../../context/NavigationContext';
import { useBookletCoupons, BookletCouponsProvider } from '../../context/BookletCouponsContext';
import PrintPage from './PrintPage';
import PrintPageAdditional from './PrintPageAdditional';
import CouponSelection from './CouponSelection';
import LogoManager from './LogoManager';
import { Booklet } from '../../types/generated.types';
import { getMyUserQuery } from '../../queries/queries';
import { useHistory } from 'react-router-dom';
import { useQuery } from '@apollo/client';

const COUPONS_PER_PAGE = {
  first: 9,
  additional: 18
};

interface BookletOption {
  value: string;
  label: string;
}

const BookletCouponsPrintContent: React.FC = () => {
  const printRef = useRef<HTMLDivElement>(null);
  const history = useHistory();
  const { data: userData, loading: userLoading } = useQuery(getMyUserQuery);
  const { setNavigationActivated } = useNavigationContext();
  const {
    selectedBookletId,
    selectedBooklet,
    bookletsData,
    bookletsLoading,
    setSelectedBookletId,
  } = useBookletCoupons();

  const [isCouponSelectionOpen, setIsCouponSelectionOpen] = useState(false);
  const [isLogoManagerOpen, setIsLogoManagerOpen] = useState(false);

  const bookletColor = selectedBooklet?.colorSalesMaterial || selectedBooklet?.colorLeft || '#dc2f34';

  useEffect(() => {
    setNavigationActivated(false);
  }, [setNavigationActivated]);

  useEffect(() => {
    if (!userLoading && !userData?.user.superAdmin) {
      alert('Du har inte behörighet att se denna sida');
      history.replace('/');
    }
  }, [userLoading, userData, history]);

  // Booklet options for select - show all booklets
  const bookletOptions: BookletOption[] = useMemo(() =>
    bookletsData?.booklets?.map((booklet: Booklet) => ({
      value: booklet._id,
      label: booklet.title,
    })) || [], [bookletsData]
  );

  const selectedBookletOption = useMemo(() => bookletOptions.find(opt => opt.value === selectedBookletId) || null, [bookletOptions, selectedBookletId]);

  const getPageStyle = () => `
      @page {
        size: var(--a4-width) var(--a4-height) !important;
        margin: 0 !important;
      }

      h2 {
        font-size: var(--h2-size) !important;
        font-family: 'Poppins' !important;
        font-weight: 800 !important;
      }

      h3 {
        font-size: var(--h3-size) !important;
        font-family: 'Poppins' !important;
        font-weight: 700 !important;
      }

      h5 {
        font-size: var(--h5-size) !important;
        font-family: 'Poppins' !important;
        font-weight: 700 !important;
      }

      span,
      p,
      li {
        font-size: var(--span-size) !important;
        font-family: 'Poppins' !important;
      }

      .print-a4 {
        box-shadow: none !important;
        margin: 0 !important;
        width: var(--a4-width) !important;
        height: var(--a4-height) !important;
        max-width: var(--a4-width) !important;
        max-height: var(--a4-height) !important;
        min-width: var(--a4-width) !important;
        min-height: var(--a4-height) !important;
        padding: var(--a4-padding) !important;

        &.additional-page {
          margin-top: 0 !important;
          padding-top: 0 !important;

          .company-coupons-list {
            height: calc(var(--a4-height) - 10mm) !important;
            min-height: calc(var(--a4-height) - 22mm) !important;
            max-height: calc(var(--a4-height) - 22mm) !important;
            grid-template-rows: repeat(6, var(--coupon-height)) !important;
          }
        }

        .company-coupons-list {
          padding: 0 var(--a4-inner-coupons-padding);
          background-color: #fff;
          width: 100%;
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: var(--coupon-gap);
          position: relative;
          z-index: 1;

          .coupon-item {
            background-color: #fff;
            padding: 4mm 3mm 2mm 3mm;
            border-radius: 3mm;
            box-sizing: border-box;
            display: flex;
            flex-direction: column;
            position: relative;
            overflow: hidden;
            cursor: pointer;
            height: var(--coupon-height);
            min-height: var(--coupon-height);
            box-shadow: none;
            text-align: center;

            &:hover .remove-btn {
              opacity: 1;
            }

            .coupon-content {
              position: relative;
              height: 100%;
              z-index: 2;
              display: flex;
              flex-direction: column;
              min-height: 0;
              align-items: center;
              justify-content: space-between;
            }

            .company-logo-container {
              width: 100%;
              height: 8mm;
              display: flex;
              align-items: center;
              justify-content: center;

              .company-logo {
                max-height: 100%;
                max-width: 100%;
                object-fit: contain;
              }
            }

            .coupon-category-group {
              position: absolute;
              top: 0;
              right: 0;
              background: #666;
              color: white;
              padding: 0.7mm 4mm !important;
              border-radius: 0 0 0 4mm !important;
              display: flex;
              flex-direction: column;
              align-items: center;
              gap: 1mm !important;
              z-index: 3;

              span {
                font-weight: bold;
                font-family: 'Poppins' !important;
              }

              p {
                margin: 0;
                font-size: 2.5mm !important;
                font-family: 'Poppins' !important;
                font-weight: 600 !important;
              }
            }

            .coupon-details {
              display: flex;
              flex-direction: column;
              min-height: 0;
              width: 100%;
              align-items: center;

              h4 {
                margin: 0;
                font-weight: 800 !important;
                font-size: var(--coupon-title-size) !important;
                text-align: center;
                font-family: 'Poppins' !important;
                text-transform: uppercase;
              }

              p {
                margin: 0;
                font-size: var(--coupon-text-size) !important;
                font-family: 'Poppins' !important;
                text-align: center;
                line-height: 1.2 !important;

                &.terms {
                  font-size: var(--coupon-text-size) !important;
                  font-family: 'Poppins' !important;
                  color: #666 !important;
                }
              }
            }

            .company-info {
              display: flex;
              flex-direction: column;
              align-items: center;

              .company-name {
                font-weight: 600;
                font-size: var(--coupon-text-size) !important;
                font-family: 'Poppins' !important;
                white-space: nowrap;
                text-align: center;
                margin: 0;
              }

              .company-address {
                font-size: var(--coupon-text-size) !important;
                font-family: 'Poppins' !important;
                color: #666 !important;
                white-space: nowrap;
                text-align: center;
                margin: 0;
              }
            }

            .coupon-bottom-lines {
              position: absolute;
              bottom: calc(-1 * (var(--a4-inner-coupons-padding) - 1mm)) !important;
              left: calc(-1 * (var(--a4-inner-coupons-padding) - 1mm)) !important;
              right: calc(-1 * (var(--a4-inner-coupons-padding) - 1mm)) !important;
              height: 25mm !important;
              pointer-events: none;
              z-index: 1;

              .gradient-overlay {
                position: absolute;
                top: 0;
                left: 0;
                right: 0;
                bottom: -1mm;
                background: linear-gradient(to top, rgba(255, 255, 255, 0.6) 0%, rgba(255, 255, 255, 1) 100%);
                pointer-events: none;
              }
            }

            .coupon-placeholder {
              display: none !important;
            }
          }
        }

        .print-footer {
          position: absolute !important;
          bottom: 0 !important;
          left: 0 !important;
          right: 0 !important;
          padding-left: calc(var(--a4-inner-coupons-padding) + 4mm) !important;
          padding-right: calc(var(--a4-inner-coupons-padding) + 4mm) !important;
          padding-bottom: 4mm !important;
          display: flex !important;
          justify-content: space-between !important;
          align-items: center !important;
          page-break-inside: avoid !important;

          .footer-left {
            display: flex !important;
            align-items: center !important;
            gap: 4mm !important;

            .brand-logo {
              height: 6.5mm !important;
              width: auto !important;
              object-fit: contain !important;
            }

            .store-buttons {
              display: flex !important;
              gap: 2mm !important;

              .store-button {
                height: 6.5mm !important;
                width: auto !important;
                object-fit: contain !important;
              }
            }
          }

          .footer-right {
            display: flex !important;
            gap: 6mm !important;

            .footer-check-item {
              display: flex !important;
              align-items: center !important;
              gap: 1mm !important;

              .checkbox {
                color: $primary-dark !important;
                font-weight: bold !important;
                flex-shrink: 0 !important;
              }

              span {
                font-size: 2.8mm !important;
                font-family: 'Poppins' !important;
                white-space: nowrap !important;
              }
            }
          }
        }
      }

      .modal-overlay,
      .modal-content,
      .modal-header,
      .modal-content.coupon-selection-modal,
      .modal-content.logo-manager-modal,
      .edit-logos-btn {
        display: none !important;
      }

      .company-logos-list {
        page-break-inside: avoid !important;
      }

      .company-logo {
        page-break-inside: avoid !important;
      }

      .remove-btn {
        display: none !important;
      }

    body {
      background: #fff !important;
    }

    .print-inner-header {
      .print-inner-header-right {
        .price-container {
          svg {
            fill: currentColor !important;
          }

          .price {
            font-size: 4.5mm !important;
            font-weight: 700 !important;
            font-family: 'Poppins' !important;
            color: #fff !important;
          }
        }
      }
    }
  `;

  return (
    <div className="booklet-coupons-print">
      <div className="select-container">
        <h3>Välj häfte</h3>
        <Select
          className="react-select-container"
          classNamePrefix="react-select"
          options={bookletOptions}
          placeholder="Välj häfte..."
          isLoading={bookletsLoading}
          value={selectedBookletOption}
          onChange={option => setSelectedBookletId(option ? option.value : null)}
        />
      </div>

      <div className="preview-section">
        <div className="preview-header">
          <h3>Förhandsgranskning</h3>
          {selectedBooklet && (
            <div className="preview-actions">
              <ReactToPrint
                trigger={() => (
                  <button type="button" className='btn primary'>
                    <span className='flex items-center gap-1'>
                      <ImFilePdf />
                      Skriv ut
                    </span>
                  </button>
                )}
                content={() => printRef.current}
                documentTitle={selectedBooklet.title}
                pageStyle={getPageStyle}
              />
            </div>
          )}
        </div>
        <div ref={printRef}>
          <PrintPage
            pageIndex={0}
            couponsPerPage={COUPONS_PER_PAGE.first}
            bookletColor={bookletColor}
            openCouponSelection={() => setIsCouponSelectionOpen(true)}
            openLogoManager={() => setIsLogoManagerOpen(true)}
          />
          <PrintPageAdditional
            pageNumber={2}
            couponsPerPage={COUPONS_PER_PAGE.additional}
            bookletColor={bookletColor}
            hasSelectedBooklet={!!selectedBooklet}
            startIndex={COUPONS_PER_PAGE.first}
            openCouponSelection={() => setIsCouponSelectionOpen(true)}
          />
        </div>
      </div>

      {isCouponSelectionOpen && (
        <CouponSelection
          isOpen={isCouponSelectionOpen}
          closeCouponSelection={() => setIsCouponSelectionOpen(false)}
          selectedBookletColor={bookletColor}
        />
      )}

      {isLogoManagerOpen && (
        <LogoManager
          closeLogoManager={() => setIsLogoManagerOpen(false)}
        />
      )}
    </div>
  );
};

const BookletCouponsPrint: React.FC = () => (
  <BookletCouponsProvider>
    <BookletCouponsPrintContent />
  </BookletCouponsProvider>
);

export default BookletCouponsPrint;
